import { Tracker, TrackerData } from "cookiesConsent/domain/Tracker"

const trackerData: ReadonlyArray<TrackerData> = [
  {
    id: `cloudflare-bot-management`,
    name: `Cloudflare Bot Management`,
    vendor: `Cloudflare`,
    category: `necessary`,
    description: `Identifies and challenges automated traffic so legitimate visitors can reach the site reliably. Required for the site to remain available and secure.`,
    cookies: [`__cf_bm`],
    retentionDays: 1,
    privacyPolicyUrl: `https://www.cloudflare.com/privacypolicy/`
  },
  {
    id: `wb-session`,
    name: `Wolf & Badger session & authentication`,
    vendor: `Wolf & Badger`,
    category: `necessary`,
    description: `Keeps you signed in, preserves your shopping bag across pages, and protects form submissions from cross-site request forgery.`,
    cookies: [`sessionid`, `csrftoken`]
  },
  {
    id: `consent-preferences`,
    name: `Consent preferences`,
    vendor: `Wolf & Badger`,
    category: `necessary`,
    description: `Remembers which consent categories you have accepted or rejected so we do not re-prompt you on every page load.`,
    cookies: [`wb-consent`],
    retentionDays: 365
  },
  {
    id: `stripe`,
    name: `Stripe`,
    vendor: `Stripe, Inc.`,
    category: `necessary`,
    description: `Used during checkout to securely process card payments and detect fraudulent transactions.`,
    cookies: [`__stripe_mid`, `__stripe_sid`],
    retentionDays: 365,
    privacyPolicyUrl: `https://stripe.com/privacy`
  },
  {
    id: `segment`,
    name: `Segment`,
    vendor: `Twilio`,
    category: `necessary`,
    description: `Routes events to downstream tools according to your consent preferences. Required so that consent-respecting routing can happen at all; the cookies do not identify you to third parties when other categories are denied.`,
    cookies: [`ajs_anonymous_id`, `ajs_user_id`],
    retentionDays: 365,
    privacyPolicyUrl: `https://segment.com/legal/privacy/`
  },
  {
    id: `dixa`,
    name: `Dixa`,
    vendor: `Dixa A/S`,
    category: `functional`,
    description: `Powers our live chat support. Pushes your authenticated session token so messages are linked to your account.`,
    privacyPolicyUrl: `https://www.dixa.com/legal/privacy-policy/`
  },
  {
    id: `hubspot-forms`,
    name: `HubSpot Forms`,
    vendor: `HubSpot, Inc.`,
    category: `functional`,
    description: `Renders embedded marketing and application forms. Sets cookies that link your form submission to your browser session.`,
    cookies: [`__hstc`, `__hssrc`, `hubspotutk`],
    retentionDays: 180,
    privacyPolicyUrl: `https://legal.hubspot.com/privacy-policy`
  },
  {
    id: `algolia-insights`,
    name: `Algolia Insights`,
    vendor: `Algolia, Inc.`,
    category: `performance`,
    description: `Records anonymous search and click events to help us improve site search ranking.`,
    cookies: [`_ALGOLIA`],
    retentionDays: 180,
    privacyPolicyUrl: `https://www.algolia.com/policies/privacy/`
  },
  {
    id: `topsort`,
    name: `Topsort`,
    vendor: `Topsort, Inc.`,
    category: `performance`,
    description: `Measures impressions and clicks on sponsored placements so we can attribute outcomes accurately.`,
    privacyPolicyUrl: `https://www.topsort.com/privacy-policy`
  },
  {
    id: `ga4`,
    name: `Google Analytics 4`,
    vendor: `Google LLC`,
    category: `performance`,
    description: `Helps us understand which pages are visited and how users navigate the site, so we can improve performance and content.`,
    cookies: [`_ga`, `_ga_*`],
    retentionDays: 395,
    privacyPolicyUrl: `https://policies.google.com/privacy`
  },
  {
    id: `ometria`,
    name: `Ometria`,
    vendor: `Ometria Ltd.`,
    category: `performance`,
    description: `Identifies signed-in users so our customer-marketing platform can attribute browsing behaviour to a customer profile for relevance and reporting.`,
    privacyPolicyUrl: `https://www.ometria.com/privacy-policy`
  },
  {
    id: `google-ads`,
    name: `Google Ads`,
    vendor: `Google LLC`,
    category: `targeting`,
    description: `Lets us show personalised ads on Google's network and measure the effectiveness of our advertising spend.`,
    cookies: [`_gcl_au`, `_gcl_aw`, `_gac_*`],
    retentionDays: 90,
    privacyPolicyUrl: `https://policies.google.com/privacy`
  },
  {
    id: `facebook`,
    name: `Meta (Facebook) Pixel & CAPI`,
    vendor: `Meta Platforms, Inc.`,
    category: `targeting`,
    description: `Used to deliver personalised ads on Facebook and Instagram and to measure conversions from those campaigns.`,
    cookies: [`_fbp`, `_fbc`],
    retentionDays: 90,
    privacyPolicyUrl: `https://www.facebook.com/privacy/policy/`
  },
  {
    id: `pinterest`,
    name: `Pinterest Tag & CAPI`,
    vendor: `Pinterest, Inc.`,
    category: `targeting`,
    description: `Tracks engagement with Pinterest ads to optimise campaigns and report on performance.`,
    cookies: [`_pin_unauth`, `_pinterest_ct_*`],
    retentionDays: 365,
    privacyPolicyUrl: `https://policy.pinterest.com/privacy-policy`
  },
  {
    id: `tiktok`,
    name: `TikTok Pixel`,
    vendor: `TikTok Ltd.`,
    category: `targeting`,
    description: `Measures the effectiveness of TikTok ads and helps deliver more relevant promotions.`,
    cookies: [`_ttp`, `_tt_enable_cookie`],
    retentionDays: 395,
    privacyPolicyUrl: `https://www.tiktok.com/legal/privacy-policy`
  },
  {
    id: `bing-ads`,
    name: `Microsoft Advertising`,
    vendor: `Microsoft Corporation`,
    category: `targeting`,
    description: `Tracks engagement with Bing and Microsoft Audience Network ads.`,
    cookies: [`_uetsid`, `_uetvid`],
    retentionDays: 395,
    privacyPolicyUrl: `https://privacy.microsoft.com/en-us/privacystatement`
  },
  {
    id: `twitter-ads`,
    name: `X (Twitter) Ads`,
    vendor: `X Corp.`,
    category: `targeting`,
    description: `Used to deliver personalised ads on X and measure how users interact with our campaigns.`,
    cookies: [`personalization_id`, `muc_ads`],
    retentionDays: 730,
    privacyPolicyUrl: `https://twitter.com/en/privacy`
  },
  {
    id: `impact`,
    name: `Impact`,
    vendor: `Impact Tech, Inc.`,
    category: `targeting`,
    description: `Attributes purchases to the affiliate or partner campaign that referred them so we can pay commissions correctly.`,
    privacyPolicyUrl: `https://impact.com/privacy-policy/`
  },
  {
    id: `innovid`,
    name: `Innovid`,
    vendor: `Innovid Corp.`,
    category: `targeting`,
    description: `Delivers and measures connected-TV and video ads served by our advertising partners.`,
    privacyPolicyUrl: `https://www.innovid.com/privacy-policy/`
  }
]

export class TrackerInventory {
  private readonly trackers: ReadonlyArray<Tracker>

  constructor(trackers: ReadonlyArray<Tracker>) {
    this.trackers = trackers
  }

  all = (): ReadonlyArray<Tracker> => this.trackers

  byCategory = (category: string): ReadonlyArray<Tracker> =>
    this.trackers.filter(tracker => tracker.category === category)
}

export class TrackerInventoryFactory {
  fromSeed = (): TrackerInventory =>
    new TrackerInventory(trackerData.map(data => new Tracker(data)))
}
